import React, { useState, useContext, useEffect, useCallback } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { betriebstageBuchService } from "../services/api";
import { weatherService } from "../services/weatherService";
import moment from "moment";

// MUI Components
import {
  Box, Button, Paper, Grid, TextField, Typography, Alert, CircularProgress, Switch, FormControlLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Stack, Divider, useTheme, AlertTitle,
  InputAdornment, Chip
} from "@mui/material";
import { alpha } from "@mui/material/styles";

// MUI Icons
import {
  SaveAlt as SaveIcon, // Changed from SaveAlt to match usage (Generate)
  ArrowBack as ArrowBackIcon, ClearOutlined as ClearIcon, AddCircleOutline as AddIcon, DeleteOutline as DeleteIcon,
  CalendarTodayOutlined as CalendarIcon, NotesOutlined as NotesIcon, ListAltOutlined as ListAltIcon,
  BuildCircleOutlined as ConveyorIcon, PlaylistAddCheckCircleOutlined as SpecialCheckIcon,
  InfoOutlined as InfoIcon, CheckCircleOutline as CheckCircleIcon, ErrorOutlineOutlined as ErrorOutlineIcon,
  VisibilityOutlined as ViewIcon, // For viewing generated entry
  SettingsApplicationsOutlined as SettingsApplicationsIcon, // For page icon
} from "@mui/icons-material";

import PageContainer from "../components/PageContainer";
// ContentSection might not be ideal here due to the two-column preview layout

const BatchBetriebstagebuchForm = () => {
  const navigate = useNavigate();
  const { currentUser, isAdmin } = useContext(AuthContext);
  const { t } = useLanguage();
  const theme = useTheme();

  const defaultConveyorTypes = ["AT", "Steigband", "Trommel", "HeckBand", "SortierBand", "MagnetSortierHaus", "MagnetHalle", "SeitenBand", "Zuführband", "TrommelWindsichter", "SpeedBand", "Airlift", "Airwheel", "Ventilator"];

  const [dateRange, setDateRange] = useState({
    startDate: moment().format("YYYY-MM-DD"),
    endDate: moment().add(4, "days").format("YYYY-MM-DD"), // Default to 5 working days
  });

  const [templateEntry, setTemplateEntry] = useState({
    conveyorChecks: defaultConveyorTypes.map(name => ({ name, functional: true, notes: "" })),
    specialChecks: [{ name: t("foreignMaterialControl", "Eigen-,Fremd, und Anlieferkontrolle"), completed: true, notes: t("noIncidents", "Keine Vorkommnisse") }, { name: t("cleaning"), completed: true, notes: "" }],
    generalNotes: "",
    creatorSignature: currentUser?.signature || null, // Pre-fill if available
    creatorName: currentUser?.name || "",
    shiftStart: "06:00", shiftEnd: "14:00", weather: t("sunny", "Sonnig"), temperature: "15",
  });

  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [successInfo, setSuccessInfo] = useState(null); // For summary message
  const [generatedEntries, setGeneratedEntries] = useState([]);

  const getDaysInRange = useCallback(() => {
    const start = moment(dateRange.startDate);
    const end = moment(dateRange.endDate);
    if (!start.isValid() || !end.isValid() || end.isBefore(start)) return [];
    const days = []; let current = start.clone();
    while (current.isSameOrBefore(end)) {
      const dayOfWeek = current.day();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Exclude weekends
        days.push({ date: current.format("YYYY-MM-DD"), dayOfWeek: t(current.format("dddd").toLowerCase(), current.format("dddd")) });
      }
      current.add(1, "days");
    }
    return days;
  }, [dateRange, t]);

  const [daysToGenerate, setDaysToGenerate] = useState(getDaysInRange());
  useEffect(() => { setDaysToGenerate(getDaysInRange()); }, [getDaysInRange]);


  const handleDateRangeChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({ ...prev, [name]: value }));
  };

  const handleTemplateChange = (section, index, field, value) => {
    setTemplateEntry(prev => {
      const newList = [...prev[section]];
      newList[index] = { ...newList[index], [field]: value };
      return { ...prev, [section]: newList };
    });
  };

  const handleTemplateFieldChange = (e) => {
    const { name, value } = e.target;
    setTemplateEntry(prev => ({ ...prev, [name]: value }));
  };

  const addSpecialCheck = () => setTemplateEntry(prev => ({ ...prev, specialChecks: [...prev.specialChecks, { name: "", completed: true, notes: "" }] }));
  const removeSpecialCheck = (index) => setTemplateEntry(prev => ({ ...prev, specialChecks: prev.specialChecks.filter((_, i) => i !== index) }));

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!dateRange.startDate || !dateRange.endDate) { setError(t("pleaseSelectDateRange")); return; }
    if (moment(dateRange.endDate).isBefore(dateRange.startDate)) { setError(t("endDateMustBeAfterStartDate")); return; }
    if (!currentUser?.signature) { setError(t("signatureRequiredForGeneration")); return; } // Ensure user has a signature

    setGenerating(true); setError(null); setSuccessInfo(null); setGeneratedEntries([]);
    try {
      const days = getDaysInRange();
      if (days.length === 0) { setError(t("noWorkdaysInRange")); setGenerating(false); return; }

      const createdEntriesLog = [];
      let coordinates = null;
      try {
        coordinates = await weatherService.getCurrentLocation();
      } catch (locationError) {
        // fallback: no location
      }
      for (const day of days) {
        // Fetch weather for this day
        let weather = templateEntry.weather;
        let temperature = templateEntry.temperature;
        try {
          const weatherData = await weatherService.getWeatherForDate(day.date, coordinates);
          weather = weatherData.weather || weather;
          temperature = weatherData.temperature?.toString() || temperature;
        } catch (err) {
          // fallback: use template values
        }
        const entryData = {
          ...templateEntry,
          date: day.date,
          creator: currentUser._id, // Ensure creator ID is passed
          weather,
          temperature,
          // creatorName and creatorSignature are already in templateEntry
        };
        try {
          const response = await betriebstageBuchService.create(entryData);
          createdEntriesLog.push({ ...day, id: response.data.entry._id, status: "success" });
        } catch (err) {
          createdEntriesLog.push({ ...day, error: err.response?.data?.message || err.message || "Failed", status: "error" });
        }
      }
      setGeneratedEntries(createdEntriesLog);
      const successCount = createdEntriesLog.filter(log => log.status === 'success').length;
      const errorCount = createdEntriesLog.length - successCount;
      setSuccessInfo(t('batchGenerationSummary', { successCount, errorCount, total: createdEntriesLog.length }));

    } catch (err) {
      console.error("Error generating entries:", err);
      setError(t("errorGeneratingEntries") + (err.message ? `: ${err.message}` : ''));
    } finally {
      setGenerating(false);
    }
  };

  if (!isAdmin()) return <PageContainer title={t("accessDenied")}><Alert severity="error">{t("adminsOnlyCanAccessBatch")}</Alert></PageContainer>;

  return (
    <PageContainer title={t("batchGenerateBetriebstagebuch")}>
      <Paper component="form" onSubmit={handleGenerate} sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="h5" component="h1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><SettingsApplicationsIcon color="primary" />{t("batchGenerateBetriebstagebuch")}</Typography>
          <Button variant="outlined" color="secondary" onClick={() => navigate("/betriebstagebuch")} disabled={generating} startIcon={<ArrowBackIcon />}>{t("backToList")}</Button>
        </Stack>

        {error && <Alert severity="error" sx={{ m: 2, mb: 0 }} onClose={() => setError(null)}>{error}</Alert>}
        {successInfo && <Alert severity={generatedEntries.some(e => e.status === 'error') ? "warning" : "success"} sx={{ m: 2, mb: 0 }}>{successInfo}</Alert>}

        <Grid container sx={{ flexGrow: 1, overflow: 'hidden' }}>
          {/* Left Panel: Configuration */}
          <Grid item xs={12} md={7} sx={{ borderRight: { md: `1px solid ${theme.palette.divider}` }, p: { xs: 2, md: 2.5 }, overflowY: { md: 'auto' }, height: { md: `calc(100vh - ${theme.mixins.toolbar.minHeight * 2 + 26}px)` } }}>
            <Typography variant="h6" gutterBottom>{t("configurationTemplate")}</Typography>

            <Paper variant="outlined" sx={{ p: 2, mb: 2.5 }}>
              <Typography variant="subtitle1" gutterBottom>{t("dateRangeForGeneration")}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><TextField fullWidth label={`${t("startDate")}*`} name="startDate" type="date" value={dateRange.startDate} onChange={handleDateRangeChange} InputLabelProps={{ shrink: true }} size="small" disabled={generating} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label={`${t("endDate")}*`} name="endDate" type="date" value={dateRange.endDate} onChange={handleDateRangeChange} InputLabelProps={{ shrink: true }} size="small" inputProps={{ min: dateRange.startDate }} disabled={generating} /></Grid>
              </Grid>
              <Alert severity="info" icon={<InfoIcon fontSize="inherit" />} sx={{ mt: 1.5, fontSize: '0.8rem' }}>{t("weekendsAreExcludedInfo")}</Alert>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 2.5 }}>
              <Typography variant="subtitle1" gutterBottom>{t("dailyEntryTemplate")}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><TextField fullWidth label={t("shiftStart")} name="shiftStart" value={templateEntry.shiftStart} onChange={handleTemplateFieldChange} size="small" disabled={generating} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label={t("shiftEnd")} name="shiftEnd" value={templateEntry.shiftEnd} onChange={handleTemplateFieldChange} size="small" disabled={generating} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label={t("weather")} name="weather" value={templateEntry.weather} onChange={handleTemplateFieldChange} size="small" disabled={generating} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label={t("temperature")} name="temperature" type="number" value={templateEntry.temperature} onChange={handleTemplateFieldChange} size="small" InputProps={{ endAdornment: <InputAdornment position="end">°C</InputAdornment> }} disabled={generating} /></Grid>
              </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 2.5 }}>
              <Typography variant="subtitle1" gutterBottom>{t("defaultConveyorChecks")}</Typography>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>{t("allConveyorsWillBeAddedInfo")}</Typography>
              <Grid container spacing={2}>
                {templateEntry.conveyorChecks.map((check, index) => (
                  <Grid item xs={12} sm={6} md={4} key={`conv-${index}`}>
                    <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, height: '100%', bgcolor: alpha(theme.palette.info.main, 0.03) }}>
                      <Typography variant="body2" fontWeight="medium">{check.name}</Typography>
                      <FormControlLabel control={<Switch size="small" checked={check.functional} onChange={e => handleTemplateChange('conveyorChecks', index, 'functional', e.target.checked)} />} label={check.functional ? t("functional") : t("notFunctional")} disabled={generating} />
                      <TextField label={t("notes")} value={check.notes} onChange={e => handleTemplateChange('conveyorChecks', index, 'notes', e.target.value)} size="small" multiline maxRows={2} fullWidth disabled={generating} />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle1">{t("defaultSpecialChecks")}</Typography>
                <Button size="small" onClick={addSpecialCheck} startIcon={<AddIcon />} disabled={generating}>{t("addCheck")}</Button>
              </Stack>
              {templateEntry.specialChecks.map((check, index) => (
                <Paper key={index} variant="outlined" sx={{ p: 1.5, mb: 1.5, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, alignItems: 'center', bgcolor: alpha(theme.palette.info.main, 0.03) }}>
                  <TextField label={t("checkName")} value={check.name} onChange={e => handleTemplateChange('specialChecks', index, 'name', e.target.value)} sx={{ flexGrow: 1 }} size="small" disabled={generating} />
                  <FormControlLabel control={<Switch size="small" checked={check.completed} onChange={e => handleTemplateChange('specialChecks', index, 'completed', e.target.checked)} />} label={check.completed ? t("completed") : t("notCompleted")} disabled={generating} />
                  <TextField label={t("notes")} value={check.notes} onChange={e => handleTemplateChange('specialChecks', index, 'notes', e.target.value)} sx={{ flexGrow: 1.5 }} size="small" multiline maxRows={2} disabled={generating} />
                  <IconButton onClick={() => removeSpecialCheck(index)} color="error" size="small" disabled={generating}><DeleteIcon /></IconButton>
                </Paper>
              ))}
              {templateEntry.specialChecks.length === 0 && <Typography variant="caption" color="text.secondary">{t("noSpecialChecksConfigured")}</Typography>}
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 2.5 }}>
              <Typography variant="subtitle1" gutterBottom>{t("defaultGeneralNotes")}</Typography>
              <TextField fullWidth label={t("notesForAllEntries")} name="generalNotes" multiline rows={3} value={templateEntry.generalNotes} onChange={handleTemplateFieldChange} variant="outlined" size="small" disabled={generating} />
            </Paper>
            {!currentUser?.signature && <Alert severity="warning" sx={{ mt: 1 }}>{t("noProfileSignatureBatchWarning")}</Alert>}
          </Grid>

          {/* Right Panel: Preview & Actions */}
          <Grid item xs={12} md={5} sx={{ p: { xs: 2, md: 2.5 }, overflowY: { md: 'auto' }, height: { md: `calc(100vh - ${theme.mixins.toolbar.minHeight * 2 + 26}px)` }, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
            <Typography variant="h6" gutterBottom>{t("previewAndGenerate")}</Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 2.5 }}>
              <Typography variant="subtitle1" gutterBottom>{t("daysToBeGenerated")}: {daysToGenerate.length}</Typography>
              {daysToGenerate.length > 0 ? (
                <TableContainer sx={{ maxHeight: 200 }}><Table size="small" stickyHeader>
                  <TableHead><TableRow><TableCell>{t("date")}</TableCell><TableCell>{t("dayOfWeek")}</TableCell></TableRow></TableHead>
                  <TableBody>{daysToGenerate.map(day => (<TableRow key={day.date}><TableCell>{day.date}</TableCell><TableCell>{day.dayOfWeek}</TableCell></TableRow>))}</TableBody>
                </Table></TableContainer>
              ) : <Typography variant="body2" color="text.secondary">{t("noWorkdaysInSelectedRange")}</Typography>}
            </Paper>

            <Button fullWidth type="submit" variant="contained" size="large" color="success" disabled={generating || !currentUser?.signature || daysToGenerate.length === 0} startIcon={generating ? <CircularProgress size={24} color="inherit" /> : <SaveIcon />}>
              {generating ? t("generatingEntries") : t("generateEntriesNow", { count: daysToGenerate.length })}
            </Button>
            {!currentUser?.signature && <Typography color="error" variant="caption" display="block" sx={{ mt: 1, textAlign: 'center' }}>{t("userSignatureRequiredToGenerate")}</Typography>}


            {generatedEntries.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, mt: 2.5 }}>
                <Typography variant="subtitle1" gutterBottom>{t("generationResults")}</Typography>
                <TableContainer sx={{ maxHeight: 300 }}><Table size="small" stickyHeader>
                  <TableHead><TableRow><TableCell>{t("date")}</TableCell><TableCell>{t("status")}</TableCell><TableCell align="right">{t("actions")}</TableCell></TableRow></TableHead>
                  <TableBody>
                    {generatedEntries.map((entry, index) => (
                      <TableRow key={index} sx={{ bgcolor: entry.status === 'error' ? alpha(theme.palette.error.main, 0.1) : alpha(theme.palette.success.main, 0.1) }}>
                        <TableCell>{entry.date} <Typography variant="caption" color="text.secondary">({entry.dayOfWeek})</Typography></TableCell>
                        <TableCell>
                          <Chip size="small" icon={entry.status === 'success' ? <CheckCircleIcon fontSize="inherit" /> : <ErrorOutlineIcon fontSize="inherit" />} label={t(entry.status)} color={entry.status === 'success' ? 'success' : 'error'} />
                          {entry.status === 'error' && <Typography variant="caption" color="error" display="block" sx={{ fontSize: '0.7rem' }}>{entry.error}</Typography>}
                        </TableCell>
                        <TableCell align="right">{entry.status === 'success' && <IconButton size="small" component={RouterLink} to={`/betriebstagebuch/${entry.id}`}><ViewIcon fontSize="inherit" /></IconButton>}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table></TableContainer>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Paper>
    </PageContainer>
  );
};

export default BatchBetriebstagebuchForm;
